/**
 * WHERE A CONTACT DOCUMENT COMES FROM — the one switch of the real-time layer without sockets.
 *
 * The contact record is moving from Firestore to Postgres (the server's
 * `docs/crm/contacts-postgres-authority-plan.md`, D2 and P1). A card, a panel or a dialog that
 * shows ONE contact used to be a Firestore listener on the document itself; once the record
 * lives in Postgres there is no document to listen to. What replaces the listener is three
 * parts, all in this folder:
 *
 *   - `contactDocumentStore`  one entry per (book, contact): the document, its mirror version,
 *                             when it was fetched — the twin of `contactLookupStore`;
 *   - `useContactSignal`      a listener on the contact's tiny SIGNAL document
 *                             (`contact-signals/{contactId}` = `{ v, at, deleted? }`), which the
 *                             server writes after every change;
 *   - `useContactDocument`    fetches `POST /contacts/get` on mount, and again exactly when the
 *                             signal's `v` passes the version it holds.
 *
 * The constant below says which of the two mechanisms ANSWERS:
 *
 *   'firestore'  today's listener only. The new path does not run.
 *   'shadow'     the listener still answers every screen; the new path runs BESIDE it, and each
 *                time both have a definite answer the two are compared and the signal→answer
 *                latency is timed (`contactDocumentShadow.ts`, a PostHog event per comparison).
 *                This is where the plan's P1 numbers come from: mismatches must read 0 outside
 *                the mirror's lag window and the latency p50 must sit under a second.
 *   'server'     the new path answers; the listener is gone. The old contacts page's live feed
 *                reads the signals instead of the documents.
 *
 * Each flip is its own one-line commit (`CONTACT_DOCUMENT_SOURCE_DEFAULT`, below), and a single
 * browser can run a mode before the build does (see "One browser may run a later mode").
 * 'firestore' → 'shadow' only once what shadow mode
 * talks to is live, in this order: the Cloud Functions (the `contact-signals` stamper
 * exclusion) and `firestore.rules` (the signal paths' read rule), then the server (it starts
 * writing signals the moment it runs; M012 is applied), then this. Flipped early, every
 * open card opens a permission-denied listener and a 404 — harmless on screen, the listener
 * still answers, but the meter records nothing and every signal write would cost two stamper
 * invocations until the exclusion ships. 'shadow' → 'server' after the shadow numbers are
 * clean (plan P1). The order is written out in docs/rules/contacts-data-access.md.
 *
 * The type annotation on the default is deliberate: without it TypeScript narrows the
 * constant to its literal and reports every other branch as unreachable.
 */
export type ContactDocumentSource = "firestore" | "shadow" | "server";

/**
 * What this build ships with: the mode of every browser that did not choose another one.
 * 'server' since 2026-10-24 — the last flip, made for the move of every contact record to
 * Postgres: from then on a Firestore contact document is a frozen copy, and this is the only
 * mode that does not read it. Before: 'shadow' for every browser from 2026-10-15 to 2026-10-19
 * (20,025 comparisons, two disagreements, both a tab waking up and both settled within seven
 * seconds), then 'firestore' again after one customer's old contacts page started hitting the
 * Firestore SDK's "INTERNAL ASSERTION FAILED: Unexpected state" 100+ times a day. Turning
 * 'shadow' off was the measurement, and it cleared the new path: the same customer hit it 111
 * times on 2026-10-22, in 'firestore' mode (docs/rules/contacts-data-access.md).
 */
export const CONTACT_DOCUMENT_SOURCE_DEFAULT: ContactDocumentSource = "server";

/**
 * ── ONE BROWSER MAY RUN A LATER MODE THAN THE BUILD'S ───────────────────────────────────
 *
 * The new path had never run in a real browser when it shipped, and a flip of the default
 * switches it on for every customer at once. So a browser can go AHEAD of the build first:
 * open any page with `?contactDocs=shadow` (or `server`) and THAT browser keeps the choice
 * for a week, or until `?contactDocs=default` takes it back. It is how the team runs
 * 'shadow' and then 'server' on the live site, on real data, before a flip makes anybody
 * else the first to do it.
 *
 * What bounds it:
 *
 *  - AHEAD ONLY. The modes are an order ('firestore' → 'shadow' → 'server'), and a choice
 *    that is not past the build's is no choice: it is not kept, and a kept one is dropped.
 *    A browser can therefore never be held on an EARLIER mode than the build's — which after
 *    the last flip would mean reading Firestore copies that are frozen.
 *  - A WEEK, so a browser somebody forgot goes back to what the customers see. The limit is
 *    enforced on what is read back: a stored date further away than a week is not a choice.
 *  - OFF THE ADDRESS once read, so a link copied from this browser does not carry it on.
 *  - READ ONCE, when this module loads: a mode never changes under a mounted hook. A browser
 *    that chose nothing is given the build's mode and is touched in no other way (one read
 *    of its storage; its address is left alone).
 *  - NOT A PERMISSION. Nobody is signed in when this runs, so it cannot be asked of the
 *    user, and it is not: it changes how this browser READS the signed-in user's own
 *    contacts, through the same endpoints and rules as everybody's. Whatever must hold for a
 *    whole OFFICE reads `CONTACT_DOCUMENT_SOURCE_DEFAULT`, never this — the pooled-book card
 *    offers its switch only when the BUILD is on 'server'.
 *
 * Anybody who has the link can use it, a customer included. What they get is the mode the
 * team is about to give everybody.
 *
 * A TEST office's pooled book can be opened this way before the last flip (a pooled record
 * exists only under 'server'), with open eyes: the choice is per browser and per device, it
 * ends after a week without a word, and a browser of that office that is NOT on 'server'
 * shows every card of the office's book as "not a contact". Acceptable for an office made of
 * test accounts, and for nothing else.
 */
export const CONTACT_DOCUMENT_SOURCE_PARAM = "contactDocs";
export const CONTACT_DOCUMENT_SOURCE_STORAGE_KEY = "webot.contactDocumentSource";
/** The value of the parameter that drops this browser's choice. */
export const CONTACT_DOCUMENT_SOURCE_TAKE_BACK = "default";
/** How long a browser keeps its choice. */
export const CONTACT_DOCUMENT_SOURCE_KEPT_FOR_MS = 7 * 24 * 60 * 60 * 1000;

const MODE_ORDER: Record<ContactDocumentSource, number> = { firestore: 0, shadow: 1, server: 2 };

const isContactDocumentSource = (value: unknown): value is ContactDocumentSource =>
  value === "firestore" || value === "shadow" || value === "server";

/** A mode a browser may hold: one PAST the build's. */
const isAheadOf = (mode: ContactDocumentSource, buildDefault: ContactDocumentSource): boolean =>
  MODE_ORDER[mode] > MODE_ORDER[buildDefault];

type ChoiceStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** The name of one `name=value` segment of a query, as it is written. */
const segmentName = (segment: string): string => {
  const at = segment.indexOf("=");
  return at === -1 ? segment : segment.slice(0, at);
};

/**
 * What the address gives the parameter, or null when it does not carry it. Read RAW — the
 * name and the value exactly as written — and by the same rule that strips it below, so a
 * spelling that sets a mode is always one that comes off the address.
 */
const askedInAddress = (search: string | null | undefined): string | null => {
  if (!search) return null;
  for (const segment of search.replace(/^[?]/, "").split("&")) {
    if (segmentName(segment) === CONTACT_DOCUMENT_SOURCE_PARAM) {
      return segment.slice(CONTACT_DOCUMENT_SOURCE_PARAM.length + 1);
    }
  }
  return null;
};

/** A storage that refuses (private mode, a full quota) costs the choice its memory, not its effect. */
const keepChoice = (storage: ChoiceStorage | null | undefined, choice: ContactDocumentSource | null, now: number): void => {
  try {
    if (choice === null) storage?.removeItem(CONTACT_DOCUMENT_SOURCE_STORAGE_KEY);
    else {
      storage?.setItem(
        CONTACT_DOCUMENT_SOURCE_STORAGE_KEY,
        JSON.stringify({ mode: choice, until: now + CONTACT_DOCUMENT_SOURCE_KEPT_FOR_MS })
      );
    }
  } catch {
    /* it still holds for this page load */
  }
};

/** What this browser kept, while it is still a choice. Anything else is nothing, and is dropped. */
const keptChoice = (
  storage: ChoiceStorage | null | undefined,
  buildDefault: ContactDocumentSource,
  now: number
): ContactDocumentSource | null => {
  const raw = storage?.getItem(CONTACT_DOCUMENT_SOURCE_STORAGE_KEY);
  if (!raw) return null;
  const kept: unknown = JSON.parse(raw);
  const { mode, until } = (kept && typeof kept === "object" ? kept : {}) as { mode?: unknown; until?: unknown };
  const withinItsWeek = typeof until === "number" && until > now && until <= now + CONTACT_DOCUMENT_SOURCE_KEPT_FOR_MS;
  if (isContactDocumentSource(mode) && withinItsWeek && isAheadOf(mode, buildDefault)) return mode;
  // Run out, overtaken by the build, or not something this code wrote: dropped, so it is not
  // read again on every load.
  keepChoice(storage, null, now);
  return null;
};

/**
 * The mode for this page load: what the address asks for (which is also kept), else what
 * this browser kept, else the build's — and never a mode that is not past the build's.
 * Never throws.
 */
export const resolveContactDocumentSource = (
  buildDefault: ContactDocumentSource,
  search: string | null | undefined,
  storage: ChoiceStorage | null | undefined,
  now: number = Date.now()
): ContactDocumentSource => {
  const asked = askedInAddress(search);
  if (asked === CONTACT_DOCUMENT_SOURCE_TAKE_BACK || isContactDocumentSource(asked)) {
    if (asked !== CONTACT_DOCUMENT_SOURCE_TAKE_BACK && isAheadOf(asked, buildDefault)) {
      keepChoice(storage, asked, now);
      return asked;
    }
    // Taken back, or a mode the build has reached already: no choice is left in this browser.
    keepChoice(storage, null, now);
    return buildDefault;
  }
  try {
    return keptChoice(storage, buildDefault, now) ?? buildDefault;
  } catch {
    return buildDefault;
  }
};

/**
 * The address's query without the parameter. Every other segment stays exactly as it was
 * written (nothing is decoded and encoded again: other code reads these raw), and a query
 * that does not carry the parameter is returned as it came, to the character.
 */
export const stripContactDocumentSourceParam = (search: string): string => {
  const segments = search.replace(/^[?]/, "").split("&");
  const others = segments.filter((segment) => segmentName(segment) !== CONTACT_DOCUMENT_SOURCE_PARAM);
  if (others.length === segments.length) return search;
  const rest = others.join("&");
  return rest === "" ? "" : `?${rest}`;
};

/**
 * Once read, the parameter comes off the address: a link copied from this browser must not
 * carry the choice into somebody else's. Runs before the router exists, so the router only
 * ever sees the clean address. An address without the parameter is not touched.
 */
const dropParamFromAddress = (): void => {
  try {
    const { pathname, search, hash } = window.location;
    const cleaned = stripContactDocumentSourceParam(search);
    if (cleaned === search) return;
    window.history.replaceState(window.history.state, "", `${pathname}${cleaned}${hash}`);
  } catch {
    /* the address keeps it; nothing else depends on this */
  }
};

const chosenInThisBrowser = (): ContactDocumentSource => {
  if (typeof window === "undefined") return CONTACT_DOCUMENT_SOURCE_DEFAULT;
  let storage: ChoiceStorage | null = null;
  let search: string | null = null;
  try {
    search = window.location.search;
    // Reading the property itself throws where storage is blocked.
    storage = window.localStorage;
  } catch {
    /* whatever was read before the throw is still used */
  }
  const mode = resolveContactDocumentSource(CONTACT_DOCUMENT_SOURCE_DEFAULT, search, storage);
  dropParamFromAddress();
  return mode;
};

export const CONTACT_DOCUMENT_SOURCE: ContactDocumentSource = chosenInThisBrowser();

if (CONTACT_DOCUMENT_SOURCE !== CONTACT_DOCUMENT_SOURCE_DEFAULT && typeof console !== "undefined") {
  // Said once per page load, so nobody forgets a browser was left on another mode.
  // eslint-disable-next-line no-console
  console.info(
    `Contact documents: '${CONTACT_DOCUMENT_SOURCE}' mode, chosen in this browser (the build's is '${CONTACT_DOCUMENT_SOURCE_DEFAULT}'). ` +
      `Open any page with ?${CONTACT_DOCUMENT_SOURCE_PARAM}=${CONTACT_DOCUMENT_SOURCE_TAKE_BACK} to take it back.`
  );
}
